import { useMutation } from "@apollo/client";
import { Avatar, Box, Button, IconButton, SvgBox, Text, Tooltip } from "@govlaunch/web";
import { useRouter } from "next/router";
import React, { forwardRef } from "react";
import { useSelfie } from "~/components/auth/Auth";
import JoinGroupMutation from "~/src/components/collection/graphql/JoinGroupMutation";
import LeaveGroupMutation from "~/src/components/collection/graphql/LeaveGroupMutation";
import { ICollectionBySlugQuery } from "~/src/components/collection/graphql/__generated__/CollectionBySlugQuery.generated";
import {
  IJoinGroupMutation,
  IJoinGroupMutationVariables,
} from "~/src/components/collection/graphql/__generated__/JoinGroupMutation.generated";
import {
  ILeaveGroupMutation,
  ILeaveGroupMutationVariables,
} from "~/src/components/collection/graphql/__generated__/LeaveGroupMutation.generated";
import { FormattedMessage, useIntl } from "react-intl";

interface ICollectionGroupCardProps {
  group: Extract<
    NonNullable<ICollectionBySlugQuery["collectionBySlug"]>["content"]["items"][0],
    { __typename: "Group" }
  >;
}

const CollectionGroupCard = forwardRef<any, ICollectionGroupCardProps>(({ group, ...props }, ref) => {
  const intl = useIntl();

  const [joinGroup, { loading }] = useMutation<IJoinGroupMutation, IJoinGroupMutationVariables>(JoinGroupMutation, {
    variables: {
      groupId: group._id,
    },
  });
  const [leaveGroup] = useMutation<ILeaveGroupMutation, ILeaveGroupMutationVariables>(LeaveGroupMutation, {
    variables: {
      groupId: group._id,
    },
  });
  const router = useRouter();
  const selfie = useSelfie();

  return (
    <Box
      as="a"
      ref={ref}
      position="relative"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      px="40px"
      borderRadius="md"
      border="1px"
      borderColor="gray.100"
      bg="white"
      minHeight="275px"
      {...props}
    >
      <Avatar src={group.logo} name={group.name} />
      <Text fontSize="lg" fontWeight="semibold" mt={5} mb={6} textAlign="center">
        {group.name}
      </Text>
      {!group.viewerIsMember && (
        <Button
          rounded="2xl"
          size="sm"
          variant="primary"
          loading={loading}
          minWidth="72px"
          onClick={() =>
            joinGroup().then(() => {
              router.push(`/group?groupSlug=${group.slug}`);
            })
          }
          disabled={
            selfie?.__typename !== "GovernmentUser" && !(selfie?.__typename === "PendingUser" && selfie.verified)
          }
        >
          <FormattedMessage defaultMessage="Join" id="P6PLpi" />
        </Button>
      )}

      {group.viewerIsMember && (
        <Box position="absolute" left={2} bottom={2}>
          <Tooltip
            label={intl.formatMessage({
              defaultMessage: "Leave Group",
              id: "aN2Gli",
            })}
            color="white"
            bg="red.600"
            rounded="md"
            py={1}
          >
            <IconButton
              aria-label={intl.formatMessage({
                defaultMessage: "Leave Group",
                id: "aN2Gli",
              })}
              icon={() => <LeaveIcon />}
              onClick={(e) => {
                e.preventDefault();

                leaveGroup();
              }}
            />
          </Tooltip>
        </Box>
      )}
    </Box>
  );
});

CollectionGroupCard.displayName = "CollectionGroupCard";

export default CollectionGroupCard;

function LeaveIcon() {
  return (
    <SvgBox
      width="32px"
      height="32px"
      viewBox="0 0 32 32"
      fill="none"
      color="gray.300"
      _hover={{
        fill: "red.600",
        color: "#fff",
      }}
    >
      <circle cx="16" cy="16" r="15.5" stroke="currentColor" />
      <path
        d="M18.6269 16L22.5674 12.0597C23.2924 11.3342 23.2924 10.1584 22.5674 9.43334C21.842 8.70744 20.6658 8.70744 19.9402 9.43334L16.0001 13.3731L12.0599 9.43334C11.3344 8.70744 10.1581 8.70744 9.43275 9.43334C8.70768 10.1584 8.70768 11.3342 9.43275 12.0597L13.3733 16L9.43275 19.9403C8.70768 20.6652 8.70768 21.8415 9.43275 22.567C10.1581 23.2925 11.3344 23.2925 12.0599 22.567L16.0001 18.6267L19.9402 22.567C20.6658 23.2925 21.842 23.2925 22.5674 22.567C23.2924 21.8415 23.2924 20.6652 22.5674 19.9403L18.6269 16Z"
        fill="currentColor"
      />
    </SvgBox>
  );
}
